import { App as AntApp, Button, Col, Collapse, Input, InputNumber, Row, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { UPLOAD_TYPES, type ParamField } from '@gw/shared';
import { useModelTest, useStartWorkflowTest } from '../../api/hooks';
import { extractErrorMessage } from '../../api/client';
import { MediaResult } from '../MediaResult';

function compact(obj: Record<string, unknown>): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    o[k] = v;
  }
  return o;
}

const STEP_TAG: Record<string, { color: string; text: string }> = {
  completed: { color: 'success', text: 'Xong' },
  failed: { color: 'error', text: 'Lỗi' },
  cancelled: { color: 'default', text: 'Đã hủy' },
};

/**
 * Chay thu WORKFLOW ngay trong editor: nhap input cong khai -> chay -> xem TUNG BUOC
 * (job con) + ket qua cuoi. Poll bang useModelTest giong model/nhom model.
 */
export default function WorkflowTestTab({ workflowId, params }: { workflowId: string; params: ParamField[] }) {
  const { message } = AntApp.useApp();
  const start = useStartWorkflowTest(workflowId);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [testId, setTestId] = useState<string>();
  const result = useModelTest(testId);
  const data = result.data;

  const fields = params.filter((f) => f.enabled !== false);
  const setValue = (name: string, v: unknown) => setValues((p) => ({ ...p, [name]: v }));

  const onRun = async () => {
    const resolved: Record<string, unknown> = {};
    for (const f of fields) {
      let v = values[f.name];
      if (f.type === 'json' && typeof v === 'string' && v.trim()) {
        try {
          v = JSON.parse(v);
        } catch {
          message.error(`Tham số "${f.name}" không phải JSON hợp lệ`);
          return;
        }
      }
      resolved[f.name] = v;
    }
    try {
      const { testId: id } = await start.mutateAsync({ input: compact(resolved) });
      setTestId(id);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const renderField = (f: ParamField) => {
    const v = values[f.name];
    if (UPLOAD_TYPES.includes(f.type)) {
      return (
        <Input
          placeholder="Dán URL công khai (https://…)"
          value={v as string}
          onChange={(e) => setValue(f.name, e.target.value)}
        />
      );
    }
    switch (f.type) {
      case 'textarea':
        return <Input.TextArea rows={3} value={v as string} onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} value={v as number} onChange={(x) => setValue(f.name, x)} />;
      case 'select':
        return (
          <Select
            style={{ width: '100%' }}
            allowClear
            value={v as string}
            onChange={(x) => setValue(f.name, x)}
            options={(f.options || []).map((o) => ({ value: o, label: String(o) }))}
          />
        );
      case 'bool':
        return <Switch checked={!!v} onChange={(x) => setValue(f.name, x)} />;
      case 'json':
        return (
          <Input.TextArea
            rows={2}
            style={{ fontFamily: 'monospace' }}
            value={v as string}
            onChange={(e) => setValue(f.name, e.target.value)}
          />
        );
      default:
        return <Input value={v as string} onChange={(e) => setValue(f.name, e.target.value)} />;
    }
  };

  return (
    <Row gutter={16}>
      <Col xs={24} lg={11}>
        <Typography.Paragraph type="secondary">
          Chạy thử đúng như phần mềm thứ 3 gọi: gửi input công khai của workflow, gateway chạy lần lượt từng bước.
        </Typography.Paragraph>
        {fields.length === 0 && (
          <Typography.Text type="warning">
            Workflow chưa khai báo tham số đầu vào — thêm ở tab "Thông tin".
          </Typography.Text>
        )}
        {fields.map((f) => (
          <div key={f.name} style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <b>{f.label || f.name}</b> {f.required && <span style={{ color: 'red' }}>*</span>} <Tag>{f.name}</Tag>
            </div>
            {renderField(f)}
          </div>
        ))}
        <Button type="primary" loading={start.isPending} onClick={onRun}>
          Chạy thử workflow
        </Button>
      </Col>

      <Col xs={24} lg={13}>
        <Typography.Title level={5}>Kết quả</Typography.Title>
        {!testId && <Typography.Text type="secondary">Bấm "Chạy thử workflow" để xem kết quả.</Typography.Text>}
        {testId && (
          <>
            <Space wrap>
              <b>Trạng thái:</b>
              {data?.status === 'succeeded' ? (
                <Tag color="success">Thành công</Tag>
              ) : data?.status === 'failed' ? (
                <Tag color="error">Thất bại</Tag>
              ) : (
                <Tag color="processing">Đang chạy…</Tag>
              )}
            </Space>
            {data?.error && (
              <Typography.Paragraph type="danger" style={{ marginTop: 8 }}>
                Lỗi ({data.error.class}): {data.error.message}
              </Typography.Paragraph>
            )}

            {!!data?.workflowSteps?.length && (
              <Table
                size="small"
                style={{ marginTop: 12 }}
                rowKey="jobId"
                pagination={false}
                dataSource={data.workflowSteps}
                columns={[
                  { title: 'Bước (node)', dataIndex: 'nodeId', width: 150 },
                  { title: 'Model đã chạy', dataIndex: 'modelName', render: (v: string | null) => v || '—' },
                  {
                    title: 'Trạng thái',
                    dataIndex: 'status',
                    width: 100,
                    render: (s: string) => {
                      const t = STEP_TAG[s];
                      return t ? <Tag color={t.color}>{t.text}</Tag> : <Tag color="processing">Đang chạy</Tag>;
                    },
                  },
                  { title: 'Lỗi', dataIndex: 'errorMessage', render: (v: string | null) => v || '' },
                ]}
              />
            )}

            {data?.status === 'succeeded' && (
              <div style={{ marginTop: 12 }}>
                <MediaResult output={data.output} />
              </div>
            )}

            {!!data?.logs?.length && (
              <Collapse
                size="small"
                style={{ marginTop: 12 }}
                items={[
                  {
                    key: 'logs',
                    label: `Nhật ký (${data.logs.length})`,
                    children: (
                      <div style={{ maxHeight: 280, overflowY: 'auto', fontSize: 12 }}>
                        {data.logs.map((l, i) => (
                          <div key={i} style={{ marginBottom: 6 }}>
                            <Tag>{l.type}</Tag> {l.message}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </>
        )}
      </Col>
    </Row>
  );
}
